// Chemin: C:\gestion-copro\src\pages\public\ConfirmationVigiePage.tsx
// Page de confirmation après inscription VIGIE
// Session 101 : Création initiale
// Session 140 : Refonte complète — Gestion flux Stripe (success / canceled / défaut)
// Session 147 : Message fondateur + typeOffre relance paiement
// Session 150 : Encadré identifiants + lien connexion copiable + email affiché
// Session 158 : Migration URL vers copro-protect.fr

import React, { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Shield, CheckCircle, XCircle, FileText, MessageSquare, BookOpen, ArrowRight, CreditCard, Loader2, RefreshCw, Star, Copy, Check, KeyRound } from 'lucide-react';
import { auth } from '../../services/firebase';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../../services/firebase';
import { getFunctions, httpsCallable } from 'firebase/functions';

type PageMode = 'loading' | 'success' | 'canceled' | 'default';

const ConfirmationVigiePage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const [mode, setMode] = useState<PageMode>('loading');
  const [isRedirecting, setIsRedirecting] = useState(false);
  const [retryError, setRetryError] = useState<string | null>(null);
  const [isFondateur, setIsFondateur] = useState(false);
  const [userEmail, setUserEmail] = useState<string | null>(null);
  const [linkCopied, setLinkCopied] = useState(false);

  const APP_URL = 'https://copro-protect.fr';

  useEffect(() => {
    const success = searchParams.get('success');
    const canceled = searchParams.get('canceled');

    if (success === 'true') {
      setMode('success');
      chargerInfosUser();
    } else if (canceled === 'true') {
      setMode('canceled');
    } else {
      setMode('default');
    }
  }, [searchParams]);

  const chargerInfosUser = async () => {
    try {
      const user = auth.currentUser;
      if (!user) return;
      setUserEmail(user.email);
      const userDoc = await getDoc(doc(db, 'users', user.uid));
      if (userDoc.exists()) {
        setIsFondateur(userDoc.data().typeOffre === 'fondateur');
      }
    } catch (err) {
      // Pas critique
    }
  };

  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(`${APP_URL}/login`);
      setLinkCopied(true);
      setTimeout(() => setLinkCopied(false), 2000);
    } catch {
      // Fallback silencieux
    }
  };

  const handleRetryPayment = async () => {
    setIsRedirecting(true);
    setRetryError(null);

    try {
      const user = auth.currentUser;
      if (!user) {
        setRetryError('Vous devez être connecté pour finaliser votre abonnement. Veuillez vous reconnecter.');
        setIsRedirecting(false);
        return;
      }

      const userDoc = await getDoc(doc(db, 'users', user.uid));
      if (!userDoc.exists()) {
        setRetryError('Profil utilisateur introuvable. Veuillez contacter le support.');
        setIsRedirecting(false);
        return;
      }

      const userData = userDoc.data();
      const coproprieteId = userData.coproprieteIds?.[0];
      if (!coproprieteId) {
        setRetryError('Aucune copropriété associée. Veuillez contacter le support.');
        setIsRedirecting(false);
        return;
      }

      const typeOffre = userData.typeOffre || 'fondateur';

      const functions = getFunctions(undefined, 'europe-west1');
      const createCheckoutSession = httpsCallable<
        { formule: string; coproprieteId: string; typeOffre: string },
        { sessionId: string; url: string }
      >(functions, 'createCheckoutSession');

      const result = await createCheckoutSession({
        formule: 'annuel',
        coproprieteId,
        typeOffre,
      });

      if (result.data.url) {
        window.location.href = result.data.url;
      } else {
        setRetryError('Impossible de créer la session de paiement. Veuillez réessayer.');
        setIsRedirecting(false);
      }
    } catch (err: any) {
      console.error('Erreur relance paiement:', err);

      if (err?.code === 'functions/resource-exhausted') {
        setRetryError('Les places fondateur sont épuisées. Veuillez vous réinscrire pour accéder au tarif normal.');
      } else {
        setRetryError('Une erreur est survenue. Veuillez réessayer ou contacter le support.');
      }

      setIsRedirecting(false);
    }
  };

  // ============================================================
  // Écran de chargement
  // ============================================================
  if (mode === 'loading') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-amber-50 via-white to-amber-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-lg p-8 max-w-md text-center">
          <Loader2 className="text-amber-600 animate-spin mx-auto mb-4" size={32} />
          <p className="text-gray-500">Chargement...</p>
        </div>
      </div>
    );
  }

  // ============================================================
  // SUCCÈS — Paiement validé
  // ============================================================
  if (mode === 'success') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-amber-50 via-white to-amber-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-lg border border-gray-200 w-full max-w-lg p-8">
          {/* Icône de succès */}
          <div className="text-center mb-6">
            <div className="w-20 h-20 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <CheckCircle className="text-emerald-600" size={40} />
            </div>
            <div className="inline-flex items-center gap-2 px-3 py-1 bg-amber-100 text-amber-800 rounded-full text-xs font-medium mb-3">
              <Shield size={14} />
              Formule VIGIE
            </div>
            <h1 className="text-2xl font-bold text-gray-900 mb-2">
              Paiement confirmé, bienvenue !
            </h1>
            <p className="text-gray-500">
              Votre abonnement est actif. Votre espace personnel est prêt à l'emploi.
            </p>
          </div>

          {/* Badge fondateur */}
          {isFondateur && (
            <div className="bg-gradient-to-r from-amber-500 to-orange-500 rounded-xl p-4 mb-6 text-white text-center">
              <div className="flex items-center justify-center gap-2 mb-1">
                <Star size={18} />
                <span className="font-bold text-sm">Membre Fondateur</span>
                <Star size={18} />
              </div>
              <p className="text-white/90 text-xs">
                Merci pour votre confiance ! Votre tarif préférentiel est verrouillé à vie.
              </p>
            </div>
          )}

          {/* Session 150 : Encadré identifiants de connexion */}
          <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 mb-6">
            <div className="flex items-center gap-2 mb-3">
              <KeyRound size={16} className="text-blue-600" />
              <p className="text-sm font-semibold text-blue-900">Vos identifiants de connexion</p>
            </div>
            {userEmail && (
              <p className="text-sm text-blue-800 mb-2">
                <span className="text-blue-600">Email :</span>{' '}
                <strong>{userEmail}</strong>
              </p>
            )}
            <p className="text-sm text-blue-800 mb-3">
              <span className="text-blue-600">Mot de passe :</span>{' '}
              celui que vous avez choisi à l'inscription
            </p>
            <div className="flex items-center gap-2">
              <p className="text-xs text-blue-600 break-all">
                Lien de connexion : <strong>{APP_URL}/login</strong>
              </p>
              <button
                onClick={handleCopyLink}
                className="flex-shrink-0 flex items-center gap-1 px-2 py-1 text-xs bg-blue-100 text-blue-700 hover:bg-blue-200 rounded transition-colors"
                title="Copier le lien"
              >
                {linkCopied ? <Check size={12} /> : <Copy size={12} />}
                {linkCopied ? 'Copié !' : 'Copier'}
              </button>
            </div>
            <p className="text-xs text-blue-500 mt-2 italic">
              Un email de bienvenue avec ces informations vous a été envoyé. Pensez à vérifier vos spams si vous ne le trouvez pas.
            </p>
          </div>

          {/* Ce que vous pouvez faire */}
          <div className="bg-gray-50 rounded-xl p-5 mb-6">
            <p className="text-sm font-semibold text-gray-900 mb-3">Ce qui vous attend :</p>
            <div className="space-y-3">
              <div className="flex items-start gap-3">
                <div className="w-8 h-8 bg-amber-100 rounded-lg flex items-center justify-center flex-shrink-0">
                  <MessageSquare size={16} className="text-amber-700" />
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-800">SENTINELLE, votre assistant IA</p>
                  <p className="text-xs text-gray-500">Posez vos questions juridiques sur la copropriété, obtenez des réponses fondées en droit.</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-8 h-8 bg-amber-100 rounded-lg flex items-center justify-center flex-shrink-0">
                  <FileText size={16} className="text-amber-700" />
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-800">Courriers professionnels</p>
                  <p className="text-xs text-gray-500">Générez des courriers structurés et juridiquement solides pour votre syndic ou promoteur.</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-8 h-8 bg-amber-100 rounded-lg flex items-center justify-center flex-shrink-0">
                  <BookOpen size={16} className="text-amber-700" />
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-800">Analyse de documents</p>
                  <p className="text-xs text-gray-500">Uploadez vos PV, contrats, appels de fonds — SENTINELLE les analyse pour vous.</p>
                </div>
              </div>
            </div>
          </div>

          {/* Bouton principal */}
          <Link
            to="/vigie"
            className="w-full py-3 px-4 rounded-lg font-semibold text-white bg-amber-600 hover:bg-amber-700 hover:shadow-lg transition-all duration-200 flex items-center justify-center gap-2"
          >
            Accéder à mon espace
            <ArrowRight size={18} />
          </Link>

          {/* Footer */}
          <div className="mt-6 pt-4 border-t border-gray-100 text-center">
            <p className="text-xs text-gray-400">
              Copro Protect — DOM TECH & SERVICES
            </p>
          </div>
        </div>
      </div>
    );
  }

  // ============================================================
  // ANNULÉ — Paiement abandonné
  // ============================================================
  if (mode === 'canceled') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-amber-50 via-white to-amber-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-lg border border-gray-200 w-full max-w-lg p-8">
          <div className="text-center mb-6">
            <div className="w-20 h-20 bg-orange-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <XCircle className="text-orange-500" size={40} />
            </div>
            <h1 className="text-2xl font-bold text-gray-900 mb-2">
              Paiement non finalisé
            </h1>
            <p className="text-gray-500">
              Votre compte a été créé mais votre abonnement n'est pas encore actif. 
              Vous pouvez relancer le paiement à tout moment.
            </p>
          </div>

          <div className="bg-orange-50 border border-orange-200 rounded-xl p-4 mb-6">
            <p className="text-sm text-orange-800">
              Aucun montant n'a été débité. Votre compte est en attente d'activation. 
              Cliquez ci-dessous pour finaliser votre abonnement via Stripe.
            </p>
          </div>

          {retryError && (
            <div className="bg-red-50 border border-red-200 rounded-xl p-4 mb-4">
              <p className="text-sm text-red-700">{retryError}</p>
            </div>
          )}

          <button
            onClick={handleRetryPayment}
            disabled={isRedirecting}
            className={`
              w-full py-3 px-4 rounded-lg font-semibold text-white
              transition-all duration-200 flex items-center justify-center gap-2
              ${isRedirecting
                ? 'bg-gray-400 cursor-not-allowed'
                : 'bg-amber-600 hover:bg-amber-700 hover:shadow-lg'
              }
            `}
          >
            {isRedirecting ? (
              <>
                <Loader2 size={18} className="animate-spin" />
                Redirection vers Stripe...
              </>
            ) : (
              <>
                <CreditCard size={18} />
                Finaliser mon abonnement
              </>
            )}
          </button>

          <div className="mt-4 text-center">
            <Link
              to="/login"
              className="text-sm text-gray-500 hover:text-gray-700 transition-colors inline-flex items-center gap-1"
            >
              <RefreshCw size={14} />
              Se reconnecter et réessayer
            </Link>
          </div>

          <div className="mt-6 pt-4 border-t border-gray-100 text-center">
            <p className="text-xs text-gray-400">
              Copro Protect — DOM TECH & SERVICES
            </p>
          </div>
        </div>
      </div>
    );
  }

  // ============================================================
  // DÉFAUT — Accès direct (rétrocompatibilité)
  // ============================================================
  return (
    <div className="min-h-screen bg-gradient-to-br from-amber-50 via-white to-amber-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-lg border border-gray-200 w-full max-w-lg p-8">
        <div className="text-center mb-6">
          <div className="w-20 h-20 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <CheckCircle className="text-emerald-600" size={40} />
          </div>
          <div className="inline-flex items-center gap-2 px-3 py-1 bg-amber-100 text-amber-800 rounded-full text-xs font-medium mb-3">
            <Shield size={14} />
            Formule VIGIE
          </div>
          <h1 className="text-2xl font-bold text-gray-900 mb-2">
            Votre espace est prêt !
          </h1>
          <p className="text-gray-500">
            Bienvenue sur Copro Protect. Votre compte est actif et vous pouvez commencer immédiatement.
          </p>
        </div>

        <div className="bg-gray-50 rounded-xl p-5 mb-6">
          <p className="text-sm font-semibold text-gray-900 mb-3">Ce qui vous attend :</p>
          <div className="space-y-3">
            <div className="flex items-start gap-3">
              <div className="w-8 h-8 bg-amber-100 rounded-lg flex items-center justify-center flex-shrink-0">
                <MessageSquare size={16} className="text-amber-700" />
              </div>
              <div>
                <p className="text-sm font-medium text-gray-800">SENTINELLE, votre assistant IA</p>
                <p className="text-xs text-gray-500">Posez vos questions juridiques sur la copropriété, obtenez des réponses fondées en droit.</p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <div className="w-8 h-8 bg-amber-100 rounded-lg flex items-center justify-center flex-shrink-0">
                <FileText size={16} className="text-amber-700" />
              </div>
              <div>
                <p className="text-sm font-medium text-gray-800">Courriers professionnels</p>
                <p className="text-xs text-gray-500">Générez des courriers structurés et juridiquement solides pour votre syndic ou promoteur.</p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <div className="w-8 h-8 bg-amber-100 rounded-lg flex items-center justify-center flex-shrink-0">
                <BookOpen size={16} className="text-amber-700" />
              </div>
              <div>
                <p className="text-sm font-medium text-gray-800">Analyse de documents</p>
                <p className="text-xs text-gray-500">Uploadez vos PV, contrats, appels de fonds — SENTINELLE les analyse pour vous.</p>
              </div>
            </div>
          </div>
        </div>

        <Link
          to="/vigie"
          className="w-full py-3 px-4 rounded-lg font-semibold text-white bg-amber-600 hover:bg-amber-700 hover:shadow-lg transition-all duration-200 flex items-center justify-center gap-2"
        >
          Accéder à mon espace
          <ArrowRight size={18} />
        </Link>

        <div className="mt-6 pt-4 border-t border-gray-100 text-center">
          <p className="text-xs text-gray-400">
            Copro Protect — DOM TECH & SERVICES
          </p>
        </div>
      </div>
    </div>
  );
};

export default ConfirmationVigiePage;
